// frontend/src/components/training/ActivityBlock.jsx
//
// Actividades que el usuario responde: confirmación, tarea, pregunta suelta y
// evaluación. La calificación la hace SIEMPRE el backend: aquí no se compara
// ninguna respuesta correcta ni se decide si aprueba.

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormLabel,
  Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { AssignmentTurnedIn as SubmitIcon, Quiz as QuizIcon } from '@mui/icons-material';

const ActivityBlock = ({ block, onSubmit, submissions }) => {
  const p = block.payload || {};
  const preguntas = block.questions || [];
  const [respuestas, setRespuestas] = useState({});
  const [texto, setTexto] = useState('');
  const [aceptado, setAceptado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const ultimo = (submissions || [])[0];
  const intentos = (submissions || []).length;
  const maximo = Number(p.max_attempts || 0);
  const sinIntentos = maximo > 0 && intentos >= maximo;

  const enviar = (payload) => {
    setEnviando(true);
    Promise.resolve(onSubmit?.(block.id, payload)).finally(() => setEnviando(false));
  };

  // ── Confirmación ──
  if (block.block_type === 'confirmation') {
    const yaConfirmado = intentos > 0;
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={yaConfirmado || aceptado} disabled={yaConfirmado}
            onChange={(e) => setAceptado(e.target.checked)} />}
          label={p.text}
        />
        {!yaConfirmado && (
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="contained" disabled={!aceptado || enviando}
              onClick={() => enviar({ answers: { confirmed: true } })}>
              Confirmar
            </Button>
          </Box>
        )}
        {yaConfirmado && <Chip size="small" color="success" label="Confirmado" sx={{ mt: 1 }} />}
      </Paper>
    );
  }

  // ── Tarea ──
  if (block.block_type === 'task') {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>{block.title || 'Tarea'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {p.instructions}
        </Typography>
        {ultimo ? (
          <Alert severity={ultimo.status === 'review_required' ? 'info' : 'success'}>
            Entregada. {ultimo.status === 'review_required' && 'Pendiente de revisión.'}
          </Alert>
        ) : (
          <Stack spacing={1}>
            <TextField multiline rows={4} fullWidth size="small"
              label="Tu respuesta" value={texto} onChange={(e) => setTexto(e.target.value)} />
            <Box>
              <Button size="small" variant="contained" startIcon={<SubmitIcon />}
                disabled={!texto.trim() || enviando}
                onClick={() => enviar({ answers: { response: texto } })}>
                Entregar
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    );
  }

  // ── Pregunta / Evaluación ──
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <QuizIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>
          {block.title || (block.block_type === 'quiz' ? 'Evaluación' : 'Pregunta')}
        </Typography>
        {p.passing_score ? (
          <Chip size="small" variant="outlined" label={`Mínimo ${p.passing_score}%`} />
        ) : null}
        {maximo > 0 && (
          <Chip size="small" variant="outlined" label={`Intentos ${intentos}/${maximo}`} />
        )}
      </Stack>

      {ultimo && (
        <Alert severity={ultimo.status === 'passed' ? 'success'
          : ultimo.status === 'review_required' ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
          {ultimo.status === 'passed' && `Aprobada con ${ultimo.percent}%`}
          {ultimo.status === 'failed' && `No aprobada (${ultimo.percent}%). `}
          {ultimo.status === 'review_required' && 'Entregada, pendiente de revisión.'}
          {ultimo.status === 'failed' && !sinIntentos && 'Puedes intentarlo de nuevo.'}
        </Alert>
      )}

      {preguntas.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Esta evaluación todavía no tiene preguntas.
        </Typography>
      )}

      <Stack spacing={2}>
        {preguntas.map((q, i) => (
          <FormControl key={q.id} fullWidth>
            <FormLabel sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              {i + 1}. {q.prompt}
            </FormLabel>
            {q.help_text && (
              <Typography variant="caption" color="text.secondary">{q.help_text}</Typography>
            )}

            {q.question_type === 'single_choice' && (
              <RadioGroup value={respuestas[q.id] || ''}
                onChange={(e) => setRespuestas({ ...respuestas, [q.id]: e.target.value })}>
                {(q.options || []).map((o) => (
                  <FormControlLabel key={String(o)} value={String(o)}
                    control={<Radio size="small" />} label={String(o)} />
                ))}
              </RadioGroup>
            )}

            {q.question_type === 'true_false' && (
              <RadioGroup row value={respuestas[q.id] || ''}
                onChange={(e) => setRespuestas({ ...respuestas, [q.id]: e.target.value })}>
                <FormControlLabel value="true" control={<Radio size="small" />} label="Verdadero" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="Falso" />
              </RadioGroup>
            )}

            {q.question_type === 'multiple_choice' && (
              <Stack>
                {(q.options || []).map((o) => {
                  const actuales = respuestas[q.id] || [];
                  return (
                    <FormControlLabel key={String(o)}
                      control={<Checkbox size="small" checked={actuales.includes(String(o))}
                        onChange={(e) => setRespuestas({
                          ...respuestas,
                          [q.id]: e.target.checked
                            ? [...actuales, String(o)]
                            : actuales.filter((x) => x !== String(o)),
                        })} />}
                      label={String(o)} />
                  );
                })}
              </Stack>
            )}

            {['short_text', 'long_text', 'number', 'url', 'date'].includes(q.question_type) && (
              <TextField size="small" fullWidth
                multiline={q.question_type === 'long_text'}
                rows={q.question_type === 'long_text' ? 3 : 1}
                type={q.question_type === 'number' ? 'number'
                  : q.question_type === 'date' ? 'date' : 'text'}
                InputLabelProps={q.question_type === 'date' ? { shrink: true } : undefined}
                value={respuestas[q.id] || ''}
                onChange={(e) => setRespuestas({ ...respuestas, [q.id]: e.target.value })} />
            )}
          </FormControl>
        ))}
      </Stack>

      {preguntas.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" startIcon={<SubmitIcon />}
            disabled={enviando || sinIntentos || ultimo?.status === 'passed'}
            onClick={() => enviar({ answers: respuestas })}>
            {ultimo?.status === 'passed' ? 'Aprobada'
              : sinIntentos ? 'Sin intentos disponibles' : 'Enviar respuestas'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

ActivityBlock.propTypes = {
  block: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  submissions: PropTypes.array,
};

export default ActivityBlock;
